/* Gleva · Direction B · gvb-gallery.js — the PDP gallery ([data-gvb-gal], snippets/gvb-product-gallery.liquid).
   ES module, loaded by the gallery snippet.
   - Phone (≤767, full-bleed pager): "2 / 5" counter and prev/next arrows follow the rail's scroll position, whoever
     scrolls it (swipe, arrows, gvb-pdp.js moving to the chosen variant's slide). The next two slides' images are
     switched from lazy to eager as the rail moves (lazy images in a side-scroller only load once swiped into view) —
     only after window load, so slides 2–3 never compete with the LCP image (speed fix #3).
   - Whole photos of mixed shapes (.gal--mixed, phone): the pager takes the current slide's height (--gal-h) once a
     swipe settles (scrollend, else 120ms after the last scroll) or at once on an arrow tap.
   - Every width: a tap / click on a slide ([data-gal-zoom]) opens a full-screen zoomable viewer — Impact's PhotoSwipe
     (PhotoSwipeLightbox from 'vendor', core from 'photoswipe' in the import map). Pinch / double-tap zoom, swipe between
     images, B bottom bar (prev · count · next), Esc / close button. Images come from each slide's data-zoom
     (2048px max, the slide's crop when it has one). The rail follows the viewer, so closing lands on the same image. */
import { PhotoSwipeLightbox } from 'vendor';

const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
/* the look-ahead loader waits for window load (apps-speed REPORT fix #3) */
let winLoaded = document.readyState === 'complete';
const afterLoad = [];
if (!winLoaded) window.addEventListener('load', () => { winLoaded = true; afterLoad.splice(0).forEach((f) => f()); }, { once: true });
const ICON = (d) => `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="${d}"/></svg>`;
const CHEV = 'm9 6 6 6-6 6';
const CLOSE_SVG = '<svg class="pswp__icn" viewBox="0 0 44 44" width="44" height="44" aria-hidden="true" focusable="false">'
  + '<circle cx="22" cy="22" r="20" fill="rgba(251,245,239,.96)"/><circle cx="22" cy="22" r="19.5" fill="none" stroke="#1A1614" stroke-opacity=".12"/>'
  + '<path d="m16 16 12 12M28 16 16 28" fill="none" stroke="#1A1614" stroke-width="1.8" stroke-linecap="round"/></svg>';

function init(gal) {
  if (gal._gvbGal) return;
  gal._gvbGal = true;
  const rail = gal.querySelector('[data-rail]');
  if (!rail) return;
  const slides = $$('.gal-slide', rail);
  const n = slides.length;
  const cur = gal.querySelector('[data-gal-i]');
  const prev = gal.querySelector('[data-gal-prev]');
  const next = gal.querySelector('[data-gal-next]');
  /* whole photos of mixed shapes (.gal--mixed, phone): each slide keeps its photo's height and the pager follows the
     current slide (CSS starts it at slide 1's height, so the first paint never shifts) */
  const mixed = gal.classList.contains('gal--mixed');
  const phone = window.matchMedia('(max-width:767px)');
  const hasEnd = 'onscrollend' in window;
  let galH = 0;
  let settleT = 0;
  let idx = 0;
  let tick = false;

  /* the slide nearest the rail's start edge */
  const slideLeft = (s) => s.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft
    - (parseFloat(getComputedStyle(rail).scrollPaddingInlineStart) || 0);
  const nearest = () => {
    let best = 0;
    let d = Infinity;
    const x = rail.scrollLeft;
    slides.forEach((s, i) => { const dd = Math.abs(slideLeft(s) - x); if (dd < d) { d = dd; best = i; } });
    return best;
  };
  const paint = () => {
    tick = false;
    if (n < 2) return;
    const max = rail.scrollWidth - rail.clientWidth;
    idx = rail.scrollLeft >= max - 2 ? n - 1 : nearest();
    if (cur) cur.textContent = String(idx + 1);
    /* the next slides are off to the side of a scroller, where lazy images wait for the swipe: fetch them ahead */
    if (max > 4) {
      const ahead = () => [idx + 1, idx + 2].forEach((i) => { const im = slides[i] && slides[i].querySelector('img[loading="lazy"]'); if (im) im.loading = 'eager'; });
      if (winLoaded) ahead(); else if (!afterLoad.includes(lookAhead)) afterLoad.push(lookAhead);
    }
    const focused = document.activeElement;
    if (prev) prev.disabled = idx <= 0;
    if (next) next.disabled = idx >= n - 1;
    /* never strand keyboard focus on a button that just got disabled */
    if (focused === prev && prev.disabled && next) next.focus({ preventScroll: true });
    else if (focused === next && next.disabled && prev) prev.focus({ preventScroll: true });
  };
  /* mixed shapes: the pager takes the settled slide's height once a swipe ends (never under the finger mid-swipe), or
     at once for an arrow tap; the track never scrolls up and down */
  const fit = (i) => {
    if (!mixed || n < 2 || !phone.matches) return;
    if (i == null) { const max = rail.scrollWidth - rail.clientWidth; i = rail.scrollLeft >= max - 2 ? n - 1 : nearest(); }
    const h = slides[i] && slides[i].offsetHeight;
    if (h && h !== galH) { galH = h; gal.style.setProperty('--gal-h', h + 'px'); }
    if (rail.scrollTop) rail.scrollTop = 0;
  };
  const go = (i, smooth) => {
    i = Math.max(0, Math.min(n - 1, i));
    const s = slides[i];
    if (!s) return;
    rail.scrollTo({ left: slideLeft(s), behavior: smooth && !reduce.matches ? 'smooth' : 'auto' });
    fit(i);
  };
  const lookAhead = () => requestAnimationFrame(paint);
  rail.addEventListener('scroll', () => {
    if (!tick) { tick = true; requestAnimationFrame(paint); }
    if (mixed && !hasEnd) { clearTimeout(settleT); settleT = setTimeout(() => fit(), 120); }
  }, { passive: true });
  if (mixed && hasEnd) rail.addEventListener('scrollend', () => fit(), { passive: true });
  window.addEventListener('resize', () => requestAnimationFrame(() => { paint(); fit(); }), { passive: true });
  if (prev) prev.addEventListener('click', () => go(idx - 1, true));
  if (next) next.addEventListener('click', () => go(idx + 1, true));
  paint();

  /* ---------------------------------------------------------------- full-screen viewer */
  let lb = null;
  const items = () => slides.map((s) => {
    const img = s.querySelector('img');
    const w = Number(s.dataset.zoomW) || (img && Number(img.getAttribute('width'))) || 1200;
    const h = Number(s.dataset.zoomH) || (img && Number(img.getAttribute('height'))) || w;
    return {
      src: s.dataset.zoom || (img && (img.currentSrc || img.src)) || '',
      width: w,
      height: h,
      msrc: (img && (img.currentSrc || img.src)) || undefined,
      alt: img ? img.alt : '',
      /* zoom-from-thumbnail only for photos that fill their frame; "whole photo on cream" and studio slides fade in.
         The viewer always shows the original photo (data-zoom), never the studio backdrop. */
      gvbThumb: img && !s.classList.contains('gal-pack') && !s.classList.contains('sb-on') ? img : null,
      thumbCropped: true,
    };
  });
  const viewer = () => {
    if (lb) return lb;
    lb = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),
      mainClass: 'gvb-pswp',
      bgOpacity: 1,
      showHideAnimationType: reduce.matches ? 'none' : 'zoom',
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      returnFocus: false,
      closeTitle: 'Close',
      closeSVG: CLOSE_SVG,
      errorMsg: 'This image could not be loaded.',
      paddingFn: (vp) => (vp.x < 768 ? { top: 64, bottom: 76, left: 0, right: 0 } : { top: 88, bottom: 104, left: 48, right: 48 }),
    });
    lb.addFilter('thumbEl', (el, data) => data.gvbThumb || undefined);
    lb.on('uiRegister', () => {
      lb.pswp.ui.registerElement({
        name: 'bottom-bar',
        className: 'pswp__bottom-bar pswp__hide-on-close',
        order: 5,
        appendTo: 'wrapper',
        html: `<div class="gvb-pswp-bar"><button type="button" data-p aria-label="Previous image">${ICON(CHEV)}</button>`
          + '<span class="gvb-pswp-count" aria-live="polite"><span data-c>1</span> / <span data-t></span></span>'
          + `<button type="button" data-n aria-label="Next image">${ICON(CHEV)}</button></div>`,
        onInit: (el, pswp) => {
          const total = pswp.getNumItems();
          el.querySelector('[data-t]').textContent = String(total);
          const bar = el.querySelector('.gvb-pswp-bar');
          if (total < 2) bar.hidden = true;
          el.querySelector('[data-p]').addEventListener('click', () => pswp.prev());
          el.querySelector('[data-n]').addEventListener('click', () => pswp.next());
          const upd = () => { el.querySelector('[data-c]').textContent = String(pswp.currIndex + 1); };
          pswp.on('change', upd);
          upd();
        },
      });
    });
    /* keep the rail on the viewer's image, so the close animation returns to a visible slide */
    lb.on('change', () => { if (lb.pswp) go(lb.pswp.currIndex, false); });
    /* focus returns to the image last shown in the viewer (on phones the rail is already on it). PhotoSwipe's own
       returnFocus would focus the slide that opened it and scroll the rail back there. */
    let lastI = 0;
    lb.on('close', () => { if (lb.pswp) lastI = lb.pswp.currIndex; });
    lb.on('destroy', () => {
      const z = slides[lastI] && slides[lastI].querySelector('[data-gal-zoom]');
      if (z) z.focus({ preventScroll: true });
    });
    lb.init();
    return lb;
  };
  rail.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-gal-zoom]');
    if (!btn || !rail.contains(btn)) return;
    const i = slides.indexOf(btn.closest('.gal-slide'));
    if (i < 0) return;
    e.preventDefault();
    viewer().loadAndOpen(i, items());
  });
}

const boot = (scope) => $$('[data-gvb-gal]', scope).forEach(init);
boot(document);
document.addEventListener('shopify:section:load', (e) => boot(e.target));
